import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaPlus, FaTimes, FaTrash, FaCheckCircle, FaClock, FaCalendarAlt } from 'react-icons/fa';
import { TimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { apiCall } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { SessionRoster as SessionRosterType, Provider, Facility, Room, SessionType } from '../../../types';
import Chip, { ChipTone } from '../../../components/Chip';

const SessionRoster: React.FC = () => {
	const { token } = useAuth();
	const [sessions, setSessions] = useState<SessionRosterType[]>([]);
	const [providers, setProviders] = useState<Provider[]>([]);
	const [facilities, setFacilities] = useState<Facility[]>([]);
	const [rooms, setRooms] = useState<Room[]>([]);
	const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);
	const [selectedFacility, setSelectedFacility] = useState<string | number>('');
	const [selectedProvider, setSelectedProvider] = useState<string | number>('');
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [editingSession, setEditingSession] = useState<SessionRosterType | null>(null);
	const [showEditScopeModal, setShowEditScopeModal] = useState<boolean>(false);
	const [editScope, setEditScope] = useState<'this' | 'future' | 'all'>('this');
	const [formData, setFormData] = useState<{
		provider: string;
		facility: string;
		room: string;
		session_type: string;
		roster_title: string;
		active_days: string;
		session_start_time: string;
		session_end_time: string;
		status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'rescheduled';
		max_patients: string;
		notes: string;
		repeat_unit: '' | 'once' | 'days' | 'weeks' | 'fortnights' | 'months';
		repeat_interval: string;
		repeat_start_date: string;
		repeat_end_date: string;
		repeat_indefinite: boolean;
	}>({
		provider: '',
		facility: '',
		room: '',
		session_type: '',
		roster_title: '',
		active_days: '',
		session_start_time: '',
		session_end_time: '',
		status: 'scheduled',
		max_patients: '',
		notes: '',
		repeat_unit: '',
		repeat_interval: '1',
		repeat_start_date: new Date().toISOString().split('T')[0],
		repeat_end_date: '',
		repeat_indefinite: false,
	});

	// Load providers
	const loadProviders = useCallback(async () => {
		try {
			const response = await apiCall('/providers/providers/', { method: 'GET' }, token || undefined);
			if (!response.ok) throw new Error(`/providers/providers/ returned ${response.status} ${response.statusText}`);
			const data = await response.json();
			const items = data.results || data;
			setProviders(Array.isArray(items) ? items : []);
		} catch (err) {
			console.error('Error loading providers:', err);
			setError(err instanceof Error ? err.message : 'Failed to load providers');
		}
	}, [token]);

	// Load facilities
	const loadFacilities = useCallback(async () => {
		try {
			const response = await apiCall('/facilities/facilities/', { method: 'GET' }, token || undefined);
			if (!response.ok) throw new Error(`/facilities/facilities/ returned ${response.status} ${response.statusText}`);
			const data = await response.json();
			const items = data.results || data;
			setFacilities(Array.isArray(items) ? items : []);
		} catch (err) {
			console.error('Error loading facilities:', err);
			setError(err instanceof Error ? err.message : 'Failed to load facilities');
		}
	}, [token]);

	// Load rooms for selected facility
	const loadRooms = useCallback(async (facilityId: string | number) => {
		if (!facilityId) {
			setRooms([]);
			return;
		}
		try {
			const response = await apiCall(`/facilities/rooms/?facility=${facilityId}`, { method: 'GET' }, token || undefined);
			if (!response.ok) throw new Error(`/facilities/rooms/ returned ${response.status} ${response.statusText}`);
			const data = await response.json();
			const items = data.results || data;
			setRooms(Array.isArray(items) ? items : []);
		} catch (err) {
			console.error('Error loading rooms:', err);
			setError(err instanceof Error ? err.message : 'Failed to load rooms');
		}
	}, [token]);

	// Load session types — scoped to the selected facility
	const loadSessionTypes = useCallback(async () => {
		if (!selectedFacility) {
			setSessionTypes([]);
			return;
		}
		try {
			const response = await apiCall(`/providers/session-types/?facility=${selectedFacility}`, { method: 'GET' }, token || undefined);
			if (!response.ok) throw new Error(`/providers/session-types/ returned ${response.status} ${response.statusText}`);
			const data = await response.json();
			const items = data.results || data;
			setSessionTypes(Array.isArray(items) ? items : []);
		} catch (err) {
			console.error('Error loading session types:', err);
			setError(err instanceof Error ? err.message : 'Failed to load session types');
		}
	}, [token, selectedFacility]);

	// Load sessions with filters
	const loadSessions = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			let url = '/providers/sessions/';
			const params = new URLSearchParams();

			if (selectedProvider) params.append('provider', selectedProvider.toString());
			if (selectedFacility) params.append('facility', selectedFacility.toString());

			if (params.toString()) {
				url += `?${params.toString()}`;
			}

			const response = await apiCall(url, { method: 'GET' }, token || undefined);
			if (!response.ok) throw new Error(`/providers/sessions/ returned ${response.status} ${response.statusText}`);
			const data = await response.json();
			const items = data.results || data;
			setSessions(Array.isArray(items) ? items : []);
		} catch (err) {
			console.error('Error loading sessions:', err);
			setError(err instanceof Error ? err.message : 'Failed to load sessions');
		} finally {
			setLoading(false);
		}
	}, [token, selectedProvider, selectedFacility]);

	useEffect(() => {
		loadProviders();
		loadFacilities();
		loadSessionTypes();
	}, [loadProviders, loadFacilities, loadSessionTypes]);

	useEffect(() => {
		loadSessions();
	}, [loadSessions]);

	useEffect(() => {
		if (formData.facility) {
			loadRooms(formData.facility);
		}
	}, [formData.facility, loadRooms]);

	const handleAddSession = () => {
		setEditingSession(null);
		setFormData({
			provider: selectedProvider?.toString() || '',
			facility: selectedFacility?.toString() || '',
			room: '',
			session_type: '',
			roster_title: '',
			active_days: '',
			session_start_time: '',
			session_end_time: '',
			status: 'scheduled',
			max_patients: '',
			notes: '',
			repeat_unit: '',
			repeat_interval: '1',
			repeat_start_date: new Date().toISOString().split('T')[0],
			repeat_end_date: '',
			repeat_indefinite: false,
		});
		setIsModalOpen(true);
	};

	const handleEditSession = (session: SessionRosterType) => {
		setEditingSession(session);
		
		// Load rooms for the facility if not already loaded
		if (session.facility) {
			loadRooms(session.facility);
		}
		
		// Convert UTC datetime to Australia/Sydney local date for form display
		const getLocalDate = (utcDateString: string | null | undefined): string => {
			if (!utcDateString) return new Date().toISOString().split('T')[0];
			
			// Create Date object from UTC string
			const utcDate = new Date(utcDateString);
			
			// Convert to Australia/Sydney timezone (UTC+11 for AEDT, UTC+10 for AEST)
			// Format: YYYY-MM-DD in local Sydney time
			const sydneyDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
			const year = sydneyDate.getFullYear();
			const month = String(sydneyDate.getMonth() + 1).padStart(2, '0');
			const day = String(sydneyDate.getDate()).padStart(2, '0');
			
			return `${year}-${month}-${day}`;
		};
		
		setFormData({
			provider: session.provider?.toString() || '',
			facility: session.facility?.toString() || '',
			room: session.room?.toString() || '',
			session_type: session.session_type?.toString() || '',
			roster_title: session.roster_title || '',
			active_days: session.active_days || '',
			session_start_time: session.start_time ? new Date(session.start_time).toTimeString().slice(0, 5) : '',
			session_end_time: session.end_time ? new Date(session.end_time).toTimeString().slice(0, 5) : '',
			status: session.status || 'scheduled',
			max_patients: session.max_patients?.toString() || '',
			notes: session.notes || '',
			repeat_unit: session.is_recurring ? 'weeks' : 'once',
			repeat_interval: '1',
			repeat_start_date: getLocalDate(session.start_time),
			repeat_end_date: getLocalDate(session.end_time),
			repeat_indefinite: false,
		});
		
		// If this is a recurring session, show scope selection modal
		if (session.is_recurring) {
			setShowEditScopeModal(true);
		} else {
			setEditScope('this');
			setIsModalOpen(true);
		}
	};

	const confirmEditScope = () => {
		setShowEditScopeModal(false);
		setIsModalOpen(true);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const target = e.target as HTMLInputElement;
		const { name, value, type, checked } = target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			setError(null);
			const url = editingSession
				? `/providers/sessions/${editingSession.id}/`
				: '/providers/sessions/';
			
			const method = editingSession ? 'PATCH' : 'POST';
			
			const submitData = {
				...formData,
				max_patients: formData.max_patients ? parseInt(formData.max_patients) : null,
				edit_scope: editingSession ? editScope : undefined,
			};
			
			await apiCall(url, {
				method,
				body: JSON.stringify(submitData),
			}, token || undefined);

			setIsModalOpen(false);
			setEditingSession(null);
			setEditScope('this');
			loadSessions();
		} catch (err) {
			console.error('Error saving session:', err);
			setError(err instanceof Error ? err.message : 'Failed to save session');
		}
	};

	const handleCancelSession = async (id: string) => {
		if (!window.confirm('Are you sure you want to cancel this session?')) {
			return;
		}
		try {
			setError(null);
			await apiCall(`/providers/sessions/${id}/`, {
				method: 'PATCH',
				body: JSON.stringify({ status: 'cancelled' }),
			}, token || undefined);
			loadSessions();
		} catch (err) {
			console.error('Error cancelling session:', err);
			setError(err instanceof Error ? err.message : 'Failed to cancel session');
		}
	};

	const handleDeleteSession = async (id: string) => {
		if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
			return;
		}
		try {
			setError(null);
			await apiCall(`/providers/sessions/${id}/`, {
				method: 'DELETE',
			}, token || undefined);
			loadSessions();
		} catch (err) {
			console.error('Error deleting session:', err);
			setError(err instanceof Error ? err.message : 'Failed to delete session');
		}
	};

	const formatTime = (dateTimeStr: string | null | undefined): string => {
		if (!dateTimeStr) return '-';
		const date = new Date(dateTimeStr);
		return date.toLocaleTimeString('en-AU', {
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const getStatusTone = (status: string): ChipTone => {
		const statusTones: Record<string, ChipTone> = {
			scheduled: 'info',
			in_progress: 'warning',
			completed: 'success',
			cancelled: 'error',
			rescheduled: 'warning',
		};
		return statusTones[status] || 'neutral';
	};

	return (
		<div className="p-6">
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaCalendarAlt className="text-primary" />
					Session Rosters
				</h1>
				<button
					className="btn btn-primary"
					onClick={handleAddSession}
					disabled={loading}
				>
					<FaPlus className="mr-2" /> Add Session
				</button>
			</div>

			{/* Filters */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
				<div className="form-control">
					<label className="label">
						<span className="label-text font-semibold">Filter by Provider</span>
					</label>
					<select
						className="select select-bordered w-full"
						value={selectedProvider}
						onChange={(e) => setSelectedProvider(e.target.value)}
					>
						<option value="">All Providers</option>
						{providers.map((provider) => (
							<option key={provider.id} value={provider.id}>
								{provider.full_name}
							</option>
						))}
					</select>
				</div>

				<div className="form-control">
					<label className="label">
						<span className="label-text font-semibold">Filter by Facility</span>
					</label>
					<select
						className="select select-bordered w-full"
						value={selectedFacility}
						onChange={(e) => setSelectedFacility(e.target.value)}
					>
						<option value="">All Facilities</option>
						{facilities.map((facility) => (
							<option key={facility.id} value={facility.id}>
								{facility.name}
							</option>
						))}
					</select>
				</div>
			</div>

			{error && (
				<div className="alert alert-error mb-4">
					<span>{error}</span>
				</div>
			)}

			{loading ? (
				<div className="flex justify-center items-center py-12">
					<div className="loading loading-spinner loading-lg"></div>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table className="table table-zebra bg-base-100/50">
						<thead>
							<tr>
								<th>Title</th>
								<th>Days</th>
								<th>Time</th>
								<th>Provider</th>
								<th>Facility</th>
								<th>Room</th>
								<th>Session Type</th>
								<th>Max Patients</th>
								<th>Status</th>
								<th>Recurring</th>
								<th className="w-32">Actions</th>
							</tr>
						</thead>
						<tbody>
							{sessions.length === 0 ? (
								<tr>
									<td colSpan={11} className="text-center py-8 text-base-content/60">
										No sessions found. Create your first session to get started.
									</td>
								</tr>
							) : (
								sessions.map((session) => (
									<tr key={session.id}>
										<td className="font-medium">{session.roster_title || '-'}</td>
										<td>
											{session.active_days ? <Chip label={session.active_days} tone="neutral" /> : '-'}
										</td>
										<td>
											<div className="flex items-center gap-2">
												<FaClock className="text-base-content/60" />
												{formatTime(session.start_time)} - {formatTime(session.end_time)}
											</div>
										</td>
										<td className="font-semibold">{session.provider_details?.full_name || '-'}</td>
										<td>{session.facility_details?.name || '-'}</td>
										<td>
											{session.room_details ? (
												<div className="flex items-center gap-2">
													<span
														className="inline-block w-3 h-3 rounded border"
														style={{ backgroundColor: session.room_details.room_type_color }}
													></span>
													{session.room_details.name}
												</div>
											) : '-'}
										</td>
										<td>
											{session.session_type_details ? (
												<Chip
													label={session.session_type_details.name}
													color={session.session_type_details.color}
												/>
											) : '-'}
										</td>
										<td className="text-center">{session.max_patients || '-'}</td>
										<td>
											<Chip label={session.status} tone={getStatusTone(session.status)} />
										</td>
										<td className="text-center">
											{session.is_recurring ? (
												<FaCheckCircle className="text-success inline" />
											) : '-'}
										</td>
										<td>
											<div className="flex gap-2">
												<button
													className="btn btn-ghost btn-xs"
													onClick={() => handleEditSession(session)}
													title="Edit"
												>
												<FaEdit className="w-4 h-4" />
											</button>
											{session.status !== 'cancelled' && session.status !== 'completed' && (
												<button
													className="btn btn-ghost btn-xs text-warning"
													onClick={() => handleCancelSession(session.id)}
													title="Deactivate"
												>
													<FaTimes className="w-4 h-4" />
													</button>
												)}
												<button
													className="btn btn-ghost btn-xs text-error"
													onClick={() => handleDeleteSession(session.id)}
													title="Delete"
												>
													<FaTrash className="w-4 h-4" />
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			)}

		{showEditScopeModal && (
			<div className="modal modal-open">
				<div className="modal-box">
					<h3 className="font-bold text-lg mb-4">Edit Recurring Session</h3>
					<p className="mb-4">This is a recurring session. What would you like to edit?</p>
					
					<div className="space-y-3">
						<label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-base-200">
							<input
								type="radio"
								name="edit_scope"
								value="this"
								checked={editScope === 'this'}
								onChange={(e) => setEditScope(e.target.value as 'this' | 'future' | 'all')}
								className="radio radio-primary"
							/>
							<div>
								<div className="font-semibold">Only this session</div>
								<div className="text-sm text-base-content/60">Changes will apply to this session only</div>
							</div>
						</label>
						
						<label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-base-200">
							<input
								type="radio"
								name="edit_scope"
								value="future"
								checked={editScope === 'future'}
								onChange={(e) => setEditScope(e.target.value as 'this' | 'future' | 'all')}
								className="radio radio-primary"
							/>
							<div>
								<div className="font-semibold">This and future sessions</div>
								<div className="text-sm text-base-content/60">Changes will apply to this session and all future occurrences</div>
							</div>
						</label>
						
						<label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-base-200">
							<input
								type="radio"
								name="edit_scope"
								value="all"
								checked={editScope === 'all'}
								onChange={(e) => setEditScope(e.target.value as 'this' | 'future' | 'all')}
								className="radio radio-primary"
							/>
							<div>
								<div className="font-semibold">All sessions in the series</div>
								<div className="text-sm text-base-content/60">Changes will apply to all sessions in this recurring series</div>
							</div>
						</label>
					</div>
					
					<div className="modal-action">
						<button
							type="button"
							className="btn btn-ghost"
							onClick={() => {
								setShowEditScopeModal(false);
								setEditingSession(null);
							}}
						>
							Cancel
						</button>
						<button
							type="button"
							className="btn btn-primary"
							onClick={confirmEditScope}
						>
							Continue
						</button>
					</div>
				</div>
			</div>
		)}

		{isModalOpen && (
			<div className="modal modal-open">
				<div className="modal-box max-w-7xl w-full max-h-[95vh] overflow-y-auto">
					<div className="flex justify-between items-center mb-3">
						<h3 className="font-bold text-xl">
							{editingSession ? 'Edit Session' : 'Create New Session'}
						</h3>
						<button
							className="btn btn-ghost btn-sm btn-circle"
							onClick={() => setIsModalOpen(false)}
						>
							<FaTimes />
						</button>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="grid grid-cols-3 gap-3">
								<div className="form-control">
									<label className="label">
										<span className="label-text">Provider *</span>
									</label>
									<select
										name="provider"
										value={formData.provider}
										onChange={handleInputChange}
										className="select select-bordered"
										required
									>
										<option value="">Select a provider...</option>
										{providers.map((provider) => (
											<option key={provider.id} value={provider.id}>
												{provider.full_name}
											</option>
										))}
									</select>
								</div>

								<div className="form-control">
									<label className="label">
										<span className="label-text">Facility *</span>
									</label>
									<select
										name="facility"
										value={formData.facility}
										onChange={handleInputChange}
										className="select select-bordered"
										required
									>
										<option value="">Select a facility...</option>
										{facilities.map((facility) => (
											<option key={facility.id} value={facility.id}>
												{facility.name}
											</option>
										))}
									</select>
								</div>

								<div className="form-control">
									<label className="label">
										<span className="label-text">Room *</span>
									</label>
									<select
										name="room"
										value={formData.room}
										onChange={handleInputChange}
										className="select select-bordered"
										required
										disabled={!formData.facility}
									>
										<option value="">Select a room...</option>
										{rooms
											.filter(room => room.is_active && room.is_bookable)
											.map((room) => (
												<option key={room.id} value={room.id}>
													{room.name} {room.number ? `(${room.number})` : ''}
												</option>
											))}
									</select>
									{formData.facility && rooms.length === 0 && (
										<label className="label">
											<span className="label-text-alt text-warning">
												No bookable rooms available for this facility
											</span>
										</label>
									)}
							</div>

							<div className="form-control">
								<label className="label">
									<span className="label-text">Session Type *</span>
									</label>
									<select
										name="session_type"
										value={formData.session_type}
										onChange={handleInputChange}
										className="select select-bordered"
										required
									>
										<option value="">Select a session type...</option>
										{sessionTypes.map((type) => (
											<option key={type.id} value={type.id}>
												{type.name}
											</option>
										))}
									</select>
								</div>

								<div className="form-control col-span-2">
									<label className="label">
										<span className="label-text">Roster Title</span>
									</label>
									<input
										type="text"
										name="roster_title"
										value={formData.roster_title}
										onChange={handleInputChange}
										className="input input-bordered"
										placeholder="e.g. Weekly Consulting Roster - Week 1"
									/>
								</div>							<div className="form-control">
								<label className="label">
									<span className="label-text">Active Days</span>
									</label>
									<input
										type="text"
										name="active_days"
										value={formData.active_days}
										onChange={handleInputChange}
										className="input input-bordered"
										placeholder="e.g. M,T,W,TH,F,S,SU"
									/>
								</div>

						<div className="form-control">
							<label className="label">
								<span className="label-text">Session Start Time *</span>
							</label>
							<div className="input input-bordered flex items-center p-0 overflow-hidden">
								<TimePickerComponent
									value={formData.session_start_time as unknown as Date}
									change={(args: any) => {
										if (args.value) {
											const date = new Date(args.value);
											const timeString = date.toTimeString().substring(0, 5);
											setFormData(prev => ({
												...prev,
												session_start_time: timeString
											}));
										}
									}}
									format="HH:mm"
									placeholder="Select start time"
									showClearButton={true}
									step={15}
								/>
							</div>
						</div>

						<div className="form-control">
							<label className="label">
								<span className="label-text">Session End Time *</span>
							</label>
							<div className="input input-bordered flex items-center p-0 overflow-hidden">
								<TimePickerComponent
									value={formData.session_end_time as unknown as Date}
									change={(args: any) => {
										if (args.value) {
											const date = new Date(args.value);
											const timeString = date.toTimeString().substring(0, 5);
											setFormData(prev => ({
												...prev,
												session_end_time: timeString
											}));
										}
									}}
									format="HH:mm"
									placeholder="Select end time"
									showClearButton={true}
									step={15}
								/>
							</div>
						</div>								<div className="form-control">
									<label className="label">
										<span className="label-text">Status</span>
									</label>
									<select
										name="status"
										value={formData.status}
										onChange={handleInputChange}
										className="select select-bordered"
									>
										<option value="scheduled">Scheduled</option>
										<option value="in_progress">In Progress</option>
										<option value="completed">Completed</option>
										<option value="cancelled">Cancelled</option>
										<option value="rescheduled">Rescheduled</option>
									</select>
								</div>

								<div className="form-control">
									<label className="label">
										<span className="label-text">Max Patients</span>
									</label>
									<input
										type="number"
										name="max_patients"
										value={formData.max_patients}
										onChange={handleInputChange}
										className="input input-bordered"
										placeholder="Maximum appointments"
									min="1"
							/>
						</div>

					<div className="col-span-3"></div>

					<div className="form-control">
						<label className="label">
							<span className="label-text">Repeat Type *</span>
								</label>
								<select
									name="repeat_unit"
									value={formData.repeat_unit}
									onChange={handleInputChange}
									className="select select-bordered"
									required
								>
									<option value="">Select repeat type...</option>
									<option value="once">Once Off (No Repeat)</option>
									<option value="days">Repeats Every X Day(s)</option>
									<option value="weeks">Repeats Every X Week(s)</option>
									<option value="fortnights">Repeats Every X Fortnight(s)</option>
									<option value="months">Repeats Every X Month(s)</option>
								</select>
							</div>								{formData.repeat_unit && formData.repeat_unit !== 'once' && (
								<>
									<div className="form-control">
										<label className="label">
											<span className="label-text">Repeat Interval *</span>
											</label>
											<input
												type="number"
												name="repeat_interval"
												value={formData.repeat_interval}
												onChange={handleInputChange}
												className="input input-bordered"
												min="1"
												max="365"
												placeholder="e.g. 1, 2, 3"
												required
											/>
										</div>

										<div className="form-control">
											<label className="label">
												<span className="label-text">Start Date *</span>
											</label>
											<input
												type="date"
												name="repeat_start_date"
												value={formData.repeat_start_date}
												onChange={handleInputChange}
												className="input input-bordered"
												required
											/>
										</div>

										<div className="form-control">
											<label className="label cursor-pointer gap-2">
												<input
													type="checkbox"
													name="repeat_indefinite"
													checked={formData.repeat_indefinite}
													onChange={handleInputChange}
													className="checkbox checkbox-primary checkbox-sm"
												/>
												<span className="label-text">Indefinite (No End Date)</span>
											</label>
										</div>

									{!formData.repeat_indefinite && (
										<div className="form-control">
											<label className="label">
												<span className="label-text">End Date *</span>
												</label>
												<input
													type="date"
													name="repeat_end_date"
													value={formData.repeat_end_date}
													onChange={handleInputChange}
													className="input input-bordered"
												min={formData.repeat_start_date}
												required
											/>
										</div>
										)}

									{formData.repeat_indefinite && (
										<div className="col-span-2 alert alert-info py-2">
												<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
												</svg>
												<span className="text-sm">Sessions will continue indefinitely. You can end or modify the series later.</span>
											</div>
										)}
									</>
								)}

							{formData.repeat_unit === 'once' && (
								<div className="form-control col-span-3">
									<label className="label">
										<span className="label-text">Session Date *</span>
										</label>
										<input
											type="date"
											name="repeat_start_date"
											value={formData.repeat_start_date}
											onChange={handleInputChange}
										className="input input-bordered"
										required
									/>
								</div>
								)}
							</div>

							<div className="modal-action">
								<button
									type="button"
									className="btn btn-ghost"
									onClick={() => setIsModalOpen(false)}
								>
									Cancel
								</button>
								<button type="submit" className="btn btn-primary">
									{editingSession ? 'Update Session' : 'Create Session'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
};

export default SessionRoster;
